/**
 * clasificadores/catalogo.js — Clasificador puro del dashboard Catálogo
 * (advisor de buy-box). Réplica del modelo (checklist docs/automatizacion-
 * deteccion-tareas.md) — molde: clasificadores/publicaciones.js.
 *
 * NO recalcula el advisor: lee las SUBLISTAS PRECALCULADAS del rollup
 * (dashboardRollups/{empresaId}__catalogo/listas/{estado}) — respeta el
 * "calcular → persistir → mostrar" del motor (spec §6). La vista manual y el
 * agente comparten esta función con el mismo input (paridad por construcción).
 *
 * 3 secciones (decisión Mariano 11-jul):
 *   bajar_precio_ganar → estado 'conviene': bajar al precio-para-ganar deja
 *                        margen ≥ corte. Plata sobre la mesa → tipo bajar_precio,
 *                        prioridad alta. Orden: Δ vs corte DESC (las más seguras).
 *   revisar_al_limite  → estado 'al-limite': margen resultante positivo pero
 *                        bajo el corte — comparar y decidir. Tipo
 *                        revisar_competitividad, prioridad media. Orden: Δ DESC
 *                        (las que están a un pelo de convenir primero).
 *   revisar_palancas   → estado 'no-conviene': ganar por precio da pérdida —
 *                        la jugada son las palancas del ganador o el costo.
 *                        Mismo tipo revisar_competitividad, prioridad BAJA
 *                        (backlog marginal, va al final).
 *
 * No generan tarea (declarados en excluidos): ganando, no-competible,
 * no-aplica, sin-vincular (para eso está el botón Sembrar), pendiente.
 *
 * Input:
 *   {
 *     catalogoListas: { conviene: [rows], 'al-limite': [rows], 'no-conviene': [rows],
 *                       ganando: [], 'no-competible': [], 'no-aplica': [],
 *                       'sin-vincular': [], pendiente: [] },
 *     config: {}  // doc de empresa (sin umbrales propios por ahora)
 *   }
 * Row del rollup: { id, externalId, titulo, sku, currentPrice, priceToWin,
 *   margenActual, margenResultante, corte, delta, semaforo, tieneCosto,
 *   hayGanador, winnerEsHermana, winnerCuenta, palancas: [{id,label,vos,ganador,gap}],
 *   catalogProductId, conexionId, ... }
 */

const itemIdDe = (r) => String(r?.externalId || r?.itemId || r?.id || '')

const urlDe = (r) => {
  if (r?.catalogProductId) return `https://www.mercadolibre.com.ar/p/${r.catalogProductId}`
  if (r?.externalId) return `https://articulo.mercadolibre.com.ar/${String(r.externalId).replace(/^MLA/, 'MLA-')}`
  return null
}

// Normaliza la fila del rollup al shape que consumen tabla + recurso de tarea.
function baseItem(r) {
  const palancas = Array.isArray(r?.palancas) ? r.palancas : []
  const gaps = palancas.filter((p) => p.gap).map((p) => p.label)
  return {
    id: itemIdDe(r),
    itemId: itemIdDe(r),
    _itemId: itemIdDe(r),
    title: r.titulo ?? null,
    permalink: urlDe(r),
    _currentPrice: r.currentPrice ?? null,
    _priceToWin: r.priceToWin ?? null,
    _difPrecio: (typeof r.currentPrice === 'number' && typeof r.priceToWin === 'number')
      ? r.priceToWin - r.currentPrice
      : null,
    _margenActual: r.margenActual ?? null,
    _margenResultante: r.margenResultante ?? null,
    _corte: r.corte ?? null,
    _delta: r.delta ?? null,
    _palancasGap: gaps,
    _palancasTotal: palancas.length,
    _winner: r.hayGanador ? (r.winnerEsHermana ? `tu cuenta${r.winnerCuenta ? ` ${r.winnerCuenta}` : ''}` : (r.winnerCuenta || 'competidor externo')) : null,
    _winnerEsHermana: !!r.winnerEsHermana,
    // `metricas` viaja tal cual al recurso de la tarea del AGENTE (recursoDe
    // hace passthrough) → el veredicto del advisor queda dentro de la tarea
    // también en el flujo automático, no solo en el manual.
    metricas: {
      precioActual: r.currentPrice ?? null,
      precioALograr: r.priceToWin ?? null,
      margenResultante: r.margenResultante ?? null,
      corte: r.corte ?? null,
      deltaVsCorte: r.delta ?? null,
      palancasFaltantes: gaps,
      teGana: r.hayGanador ? (r.winnerCuenta || 'competidor externo') : null,
    },
  }
}

const deltaDe = (r) => (typeof r?.delta === 'number' ? r.delta : -Infinity)

export function clasificarCatalogo({ catalogoListas = {}, config = {} } = {}) { // eslint-disable-line no-unused-vars
  const lista = (k) => Array.isArray(catalogoListas[k]) ? catalogoListas[k] : []

  // ── bajar_precio_ganar (conviene) — Δ vs corte DESC: las más seguras primero ──
  const bajar_precio_ganar = lista('conviene')
    .map((r) => ({
      ...baseItem(r),
      _motivo: `Bajar a $${Math.round(r.priceToWin ?? 0).toLocaleString('es-AR')} gana el buy-box con margen ${r.margenResultante != null ? (r.margenResultante * 100).toFixed(1) + '%' : '—'} (corte ${r.corte != null ? (r.corte * 100).toFixed(1) + '%' : '—'})`,
    }))
    .sort((a, b) => (b._delta ?? -Infinity) - (a._delta ?? -Infinity))

  // ── revisar_al_limite — Δ DESC (las más cerca de convenir primero) ──
  const revisar_al_limite = lista('al-limite')
    .map((r) => ({
      ...baseItem(r),
      _motivo: `Ganar deja margen ${r.margenResultante != null ? (r.margenResultante * 100).toFixed(1) + '%' : '—'} — bajo el corte por ${r.delta != null ? Math.abs(r.delta * 100).toFixed(1) + ' pp' : '—'}`,
    }))
    .sort((a, b) => (b._delta ?? -Infinity) - (a._delta ?? -Infinity))

  // ── revisar_palancas (no conviene) — backlog marginal, prioridad baja ──
  const revisar_palancas = lista('no-conviene')
    .map((r) => ({
      ...baseItem(r),
      _motivo: r.tieneCosto === false
        ? 'Sin costo cargado — el margen resultante no se puede evaluar'
        : `Ganar por precio da pérdida (${r.margenResultante != null ? (r.margenResultante * 100).toFixed(1) + '%' : '—'} vs corte ${r.corte != null ? (r.corte * 100).toFixed(1) + '%' : '—'})${(baseItem(r)._palancasGap.length ? ` — palancas faltantes: ${baseItem(r)._palancasGap.join(', ')}` : '')}`,
    }))
    .sort((a, b) => (b._delta ?? -Infinity) - (a._delta ?? -Infinity))

  return {
    secciones: {
      bajar_precio_ganar,
      revisar_al_limite,
      revisar_palancas,
    },
    analisis: {
      kpis: {
        conviene: bajar_precio_ganar.length,
        alLimite: revisar_al_limite.length,
        noConviene: revisar_palancas.length,
        yaGanas: lista('ganando').length,
      },
    },
    excluidos: {
      // Estados sin tarea, declarados (no silenciosos)
      yaGanas: lista('ganando').length,
      noCompetible: lista('no-competible').length,
      noAplica: lista('no-aplica').length,
      sinVincular: lista('sin-vincular').length,
      pendiente: lista('pendiente').length,
      rollupCargado: Object.keys(catalogoListas).length > 0,
    },
  }
}
